import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toImg } from "../../utils/img";
import Api from "../../services/api.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarDay } from "@fortawesome/free-solid-svg-icons";
import Helper from "../../services/helper.service";

function formatISO(d, lang) {
  const dt = new Date(d);
  return dt.toLocaleDateString(lang, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default function EventPublicDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language?.slice(0, 2) || "fr");
  const [errors, setErrors] = useState({});

  const validate = (data) => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!data.name?.trim()) e.name = t("errors.required", "Required");
    if (!data.phone?.trim()) e.phone = t("errors.required", "Required");
    if (!data.email?.trim()) e.email = t("errors.required", "Required");
    else if (!emailRe.test(String(data.email))) e.email = t("errors.wrongEmail", "Invalid email");
    if (!desiredDate) e.desiredDateText = t("errors.pickDate", "Please choose a date");

    return e;
  };
  // small inline i18n picker (no external util)
  const pick = (obj) =>
    obj?.[lang] ?? obj?.fr ?? obj?.en ?? obj?.de ?? "";

  const [ev, setEv] = useState(null);
  const [desiredDate, setDesiredDate] = useState("");

  useEffect(() => {
    (async () => {
      const res = await Api.getPublicEvent(slug);
      if (res?.data?.status) setEv(res.data.data);
    })();
  }, [slug]);

  const dates = useMemo(() => (ev?.dates || []), [ev]);

  if (!ev) return null;

  const title = pick(ev?.name);
  const subtitle = pick(ev?.shortDescription);
  const longDesc = pick(ev?.longDescription);

  return (
    <div className="bg-backgroundColor">
      {/* HERO */}
      <div className="relative w-full h-[260px] md:h-[340px]">
        {ev?.heroImage && (
          <img
            src={toImg(ev.heroImage)}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative max-w-6xl mx-auto h-full flex items-end px-4 pb-6">
          <div className="text-white">
            <h1 className="text-2xl md:text-4xl font-extrabold">{title}</h1>
            <p className="mt-2 opacity-90">{subtitle}</p>
            <div className="mt-3 flex gap-2">
              {ev?.tags?.map((tTag) => (
                <span
                  key={tTag}
                  className="px-2 py-1 rounded-full text-xs bg-white text-description border border-main"
                >
                  <span className="opacity-60">{tTag}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-3 gap-8">
        {/* LEFT */}
        <div className="md:col-span-2 space-y-6">
          <section className="bg-white border border-main p-6 shadow-sm">
            <h2 className="text-lg font-extrabold mb-3">
              {t("events.eventPublic.about")}
            </h2>
            <div
              className="prose prose-sm max-w-none text-description/80"
              dangerouslySetInnerHTML={{ __html: longDesc }}
            />
          </section>

          {!!dates.length && (
            <section className="bg-white border border-main p-6 shadow-sm">
              <h3 className="text-lg font-extrabold mb-4">
                {t("events.eventPublic.availableDates")}
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-3 md:gap-12">
                {dates.map((d) => {
                  const active = desiredDate === d;
                  return (
                    <button
                      type="button"
                      key={d}
                      className={`flex items-center gap-2 px-3 py-2 border border-main ${active ? "ring-2 ring-main/20" : ""} bg-white text-left text-sm`}
                      onClick={() => setDesiredDate(d)}
                      aria-pressed={active}
                    >
                      <span className="w-5 h-5 text-[16px] text-xl text-main flex items-center justify-center rounded-full">
                        <FontAwesomeIcon icon={faCalendarDay} className="text-[11px]" />
                      </span>
                      <span className="font-medium">{formatISO(d, lang)}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* RIGHT – replace with your existing contact form if needed */}
        <aside className="md:col-span-1">
          <div className="bg-white p-6 shadow-xl sticky top-6">
            <h3 className="text-lg font-extrabold mb-2">
              {t("events.eventPublic.wantToParticipate")}
            </h3>
            <p className="text-sm text-description/70 mb-4">
              {t("events.eventPublic.fillForm")}
            </p>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                const formData = Object.fromEntries(fd.entries());

                const eObj = validate(formData);
                if (Object.keys(eObj).length) {
                  setErrors(eObj);
                  return; // don't call API
                }

                try {
                  const payload = { ...formData, eventSlug: ev.slug, desiredDate, lang };
                  const res = await Api.sendEventContact(payload);
                  const ok = res?.data?.data?.status || res?.status === 200;
                  if (ok) {
                    Helper.handleApiResponse(res, t('responses.messageContactSent'), t('responses.messageContactError'));
                    setDesiredDate("");
                    setErrors({});
                  }
                } catch (err) {
                  console.error(err);
                  alert(t("events.eventPublic.toast.error"));
                }
              }}
              className="space-y-4"
            >
              {/* Name */}
              <div className="flex flex-col gap-1">
                <label htmlFor="name" className="text-sm font-medium text-description">
                  {t("events.eventPublic.form.name")}
                </label>
                <input
                  id="name"
                  name="name"
                  placeholder={t("events.eventPublic.form.name")}
                  className="w-full border border-borderColor/80 rounded px-3 py-2"
                />
                {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
              </div>

              {/* Firstname */}
              <div className="flex flex-col gap-1">
                <label htmlFor="firstname" className="text-sm font-medium text-description">
                  {t("events.eventPublic.form.firstname")}
                </label>
                <input
                  id="firstname"
                  name="firstname"
                  placeholder={t("events.eventPublic.form.firstname")}
                  className="w-full border border-borderColor/80 rounded px-3 py-2"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1">
                <label htmlFor="email" className="text-sm font-medium text-description">
                  {t("events.eventPublic.form.email")}
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  placeholder={t("events.eventPublic.form.email")}
                  className="w-full border border-borderColor/80 rounded px-3 py-2"
                />
                {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-1">
                <label htmlFor="phone" className="text-sm font-medium text-description">
                  {t("events.eventPublic.form.phone")}
                </label>
                <input
                  id="phone"
                  name="phone"
                  placeholder={t("events.eventPublic.form.phone")}
                  className="w-full border border-borderColor/80 rounded px-3 py-2"
                />
                {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
              </div>

              {/* Desired date (select) */}
              <div className="flex flex-col gap-1">
                <label htmlFor="desiredDateText" className="text-sm font-medium text-description">
                  {t("events.eventPublic.form.desiredDate")}
                </label>
                <select
                  id="desiredDateText"
                  name="desiredDateText"
                  value={desiredDate || ""}
                  onChange={(e) => setDesiredDate(e.target.value)}
                  className="w-full border border-borderColor/80 rounded px-3 py-2 bg-white"
                  aria-label={t("events.eventPublic.form.desiredDate")}
                >
                  <option value="" disabled>
                    {t("events.eventPublic.form.desiredDate")}
                  </option>
                  {dates.map((d) => (
                    <option key={d} value={d}>
                      {formatISO(d, lang)}
                    </option>
                  ))}
                </select>
                {errors.desiredDateText && <p className="text-xs text-red-600">{errors.desiredDateText}</p>}
              </div>

              {/* Submit */}
              <button className="w-full bg-main hover:bg-main-dark text-white px-4 py-2 transition">
                {t("events.eventPublic.form.send")}
              </button>
            </form>
          </div>
        </aside>
      </div>
    </div>
  );
}